import React, {ReactElement} from 'react';

import {canRequestAccess, requestAccess} from 'auth/RequestAccess';
import DataConnector from 'connect/DataConnector';
import {StorableSummary} from 'constants/SampleConstants';
import {NavStoreSource} from 'constants/SourceConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import {LoadingFileScreen} from 'nav/full-screen/LoadingFullScreen';
import NavStore from 'nav/NavStore';

import * as StorableActions from '../StorableActions';
import * as StorableAuth from '../storables/StorableAuth';
import StorableStore, {GridWithLoading} from '../StorableStore';

class GridContainerConnector extends DataConnector<GridContainerProps, EnhancedProps> {
  override stores = [NavStore, StorableStore];

  enhanceProps(): EnhancedProps {
    const grid = this.storableStore().getGrid(this.props.gridId);
    return {
      authErrorMessage: this.storableStore().getAuthErrorMessage(this.props.gridId),
      grid,
      parentStorable:
        grid && grid.parentStorableId ? this.storableStore().getStorableSummary(grid.parentStorableId) : null,
      registry: grid && this.navStore().getSource(grid.storableSchema.registryId),
    };
  }
}

export interface GridTabChildProps {
  folderId: string | null;
  grid: GridWithLoading;
  parentStorable: StorableSummary | null;
}

interface GridContainerProps {
  gridId: string;
  children: (props: GridTabChildProps) => ReactElement;
}
interface EnhancedProps {
  authErrorMessage?: string;
  grid: GridWithLoading | null;
  parentStorable: StorableSummary | null;
  registry: NavStoreSource | null;
}

export class GridContainer extends React.Component<GridContainerProps & EnhancedProps> {
  override componentDidMount(): void {
    StorableActions.fetchGrid(this.props.gridId);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  override componentDidUpdate(prevProps: GridContainerProps & EnhancedProps, prevState: any): void {
    if (prevProps.grid && !this.props.grid) {
      // Grid was invalidated - refetch to get the updated grid
      StorableActions.fetchGrid(this.props.gridId);
    }

    const inaccessibleInfo = StorableStore.getInaccessibleInfo(this.props.gridId);
    if (inaccessibleInfo) {
      if (canRequestAccess(inaccessibleInfo)) {
        requestAccess(inaccessibleInfo);
        FileBrowserActions.removeFromWorkspace(this.props.gridId);
      }
    }
  }

  override render(): JSX.Element {
    if (this.props.authErrorMessage) {
      return <StorableAuth.ErrorBanner message={this.props.authErrorMessage} />;
    }
    if (!this.props.grid) {
      return <LoadingFileScreen />;
    }
    const folderId = this.props.registry?.rootFolder.api_identifier ?? null;
    const childProps = {folderId, grid: this.props.grid, parentStorable: this.props.parentStorable};
    return this.props.children(childProps);
  }
}

export default GridContainerConnector.connect(GridContainer);
